// screens/staff/StaffApp.tsx
//
// W6 v1 Session 1 — root for the /staff/* surface.
//
// Owns the staff-side auth state (separate localStorage key from the student
// app: asc_staff_auth_v1 vs asc_auth_v1). The StaffApp wraps a Routes block
// that knows about all staff sub-routes; if no JWT is stored, all routes
// route to /staff/login.
//
// **W6 v1 Session 2 fix — fresh-login redirect**
// `defaultLanding` depends on `me.role` to choose between /staff/home
// (instructors / titolare) and /staff/studenti (segreteria). On fresh
// login, `me` is null until we call /api/staff/me; if we render `<Routes>`
// before the fetch resolves, the `<Navigate to={defaultLanding}>` fires
// too early and an instructor lands on /staff/studenti. To prevent that,
// we hoist the `me` fetch up to this component, render a formal
// "Caricamento…" skeleton while it resolves, and only mount `<Routes>`
// once the role is known. The single source-of-truth fetch lives here;
// `StaffDashboardLayout` receives `me` as a prop and skips its own fetch.
//
// **W6 v1 Session 3 fix — RequireStaffRole route guard**
// The side-nav already hides pedagogy routes for the segreteria role, but
// a savvy front-desk user could paste `/staff/lacune` (or `/staff/home`,
// `/staff/metriche`, `/staff/studenti/<id>`) into the address bar. The BE
// returns 403, but the FE briefly renders the page chrome + an error
// toast — pedagogical surface visible-but-empty is still a leak signal
// in user testing. `RequireStaffRole` short-circuits the render at the
// route level: segreteria gets redirected to `/staff/studenti` (the
// roster screen they own) without ever mounting the wrapped page.

import { useState, useCallback, useEffect, type JSX, type ReactNode } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import StaffLoginScreen from './StaffLoginScreen';
import { StaffDashboardLayout } from './StaffDashboardLayout';
import SegreteriaStudentiScreen from './SegreteriaStudentiScreen';
import SegreteriaCodiciScreen from './SegreteriaCodiciScreen';
import SegreteriaFrequenzeScreen from './SegreteriaFrequenzeScreen';
import InstructorHomeScreen from './InstructorHomeScreen';
import StudentDetailScreen from './StudentDetailScreen';
import LacuneQueueScreen from './LacuneQueueScreen';
import MetricheScreen from './MetricheScreen';
import ClassPulseScreen from './ClassPulseScreen';
import BozzeQueueScreen from './BozzeQueueScreen';
import ProfFeedbackQueueScreen from './ProfFeedbackQueueScreen';
import { getStaffToken } from '../../api/staff-client';
import { staffApi, type StaffMe, type StaffRole } from '../../api/staff';
import { useTokens } from '../../state/theme';

export default function StaffApp(): JSX.Element {
  const tokens = useTokens();
  const [hasToken, setHasToken] = useState<boolean>(!!getStaffToken());
  const [me, setMe] = useState<StaffMe | null>(null);
  const [meError, setMeError] = useState<boolean>(false);

  const handleAuthed = useCallback(() => {
    setHasToken(true);
    setMe(null);  // force re-fetch
    setMeError(false);
  }, []);

  const handleMeChange = useCallback((next: StaffMe | null) => {
    setMe(next);
    if (!next) {
      setHasToken(false);
      setMeError(false);
    }
  }, []);

  // Fetch `me` once we have a token and haven't yet resolved the role.
  // The defaultLanding redirect must NOT fire until this resolves — see
  // the header comment.
  useEffect(() => {
    if (!hasToken || me || meError) return;
    let alive = true;
    staffApi.me()
      .then((m) => { if (alive) setMe(m); })
      .catch(() => { if (alive) setMeError(true); });
    return () => { alive = false; };
  }, [hasToken, me, meError]);

  if (!hasToken) {
    return (
      <Routes>
        <Route path="/login" element={<StaffLoginScreen onAuthed={handleAuthed} />} />
        <Route path="*" element={<Navigate to="/staff/login" replace />} />
      </Routes>
    );
  }

  // `me` fetch failed — let the layout render its error UI (formal
  // Italian "Sessione scaduta" + "Torna all'accesso"). We pass me={null}
  // and the layout's own fetch will retry / surface the error. This
  // matches the prior behavior on /me failure.
  if (meError) {
    return (
      <Routes>
        <Route path="*" element={<StaffDashboardLayout me={null} onMeChange={handleMeChange}><div /></StaffDashboardLayout>} />
      </Routes>
    );
  }

  // Authed but `me` not yet resolved — render formal Italian skeleton.
  // We deliberately do NOT mount `<Routes>` yet, so any `<Navigate>` that
  // depends on `me.role` cannot fire prematurely.
  if (!me) {
    return (
      <div
        data-testid="staff-app-loading"
        style={{
          minHeight:      '100vh',
          background:     tokens.bg,
          color:          tokens.textMuted,
          display:        'flex',
          alignItems:     'center',
          justifyContent: 'center',
          fontFamily:     tokens.fonts.body,
        }}
      >
        Caricamento…
      </div>
    );
  }

  // Role-aware default landing — `me` is now guaranteed non-null here.
  //   - segreteria → /staff/studenti (Session 1 default — they don't see /home)
  //   - instructors / titolare → /staff/home (Session 2 — pedagogical dashboard)
  const defaultLanding = me.role !== 'segreteria' ? '/staff/home' : '/staff/studenti';

  // Pedagogy-cleared roles (titolare + instructor_*). Segreteria is walled
  // off from these routes at both BE (403 from `requireStaffRole(...)`) and
  // here at the FE (URL-paste protection — see RequireStaffRole below).
  const PEDAGOGY_ROLES: readonly StaffRole[] = ['titolare', 'instructor_teoria', 'instructor_guida'];

  // Code-minting roles (titolare + segreteria). Mirrors the BE gate on the
  // /api/staff/invite-codes endpoints (`requireStaffRole(...ROLE_SEGRETERIA_OR_TITOLARE)`
  // in index.ts) and the side-nav allow-list for "Codici invito". Instructors
  // get a 403 from the BE, but without the route guard a pasted /staff/codici
  // URL still rendered the page chrome — same leak shape as the pedagogy routes.
  const ADMIN_ROLES: readonly StaffRole[] = ['titolare', 'segreteria'];

  return (
    <Routes>
      <Route path="/login" element={<Navigate to={defaultLanding} replace />} />
      <Route path="/" element={<Navigate to={defaultLanding} replace />} />
      <Route path="/home"     element={
        <RequireStaffRole me={me} roles={PEDAGOGY_ROLES} fallbackTo="/staff/studenti">
          <StaffDashboardLayout me={me} onMeChange={handleMeChange}><InstructorHomeScreen /></StaffDashboardLayout>
        </RequireStaffRole>
      } />
      <Route path="/studenti" element={<StaffDashboardLayout me={me} onMeChange={handleMeChange}><SegreteriaStudentiScreen /></StaffDashboardLayout>} />
      <Route path="/studenti/:studentId" element={
        <RequireStaffRole me={me} roles={PEDAGOGY_ROLES} fallbackTo="/staff/studenti">
          <StaffDashboardLayout me={me} onMeChange={handleMeChange}><StudentDetailScreen /></StaffDashboardLayout>
        </RequireStaffRole>
      } />
      <Route path="/codici"   element={
        <RequireStaffRole me={me} roles={ADMIN_ROLES} fallbackTo="/staff/studenti">
          <StaffDashboardLayout me={me} onMeChange={handleMeChange}><SegreteriaCodiciScreen   /></StaffDashboardLayout>
        </RequireStaffRole>
      } />
      {/* /frequenze is deliberately NOT wrapped: attendance is all-staff by design (side-nav allow-list + BE ROLE_ALL_STAFF). */}
      <Route path="/frequenze" element={<StaffDashboardLayout me={me} onMeChange={handleMeChange}><SegreteriaFrequenzeScreen /></StaffDashboardLayout>} />
      <Route path="/lacune"    element={
        <RequireStaffRole me={me} roles={PEDAGOGY_ROLES} fallbackTo="/staff/studenti">
          <StaffDashboardLayout me={me} onMeChange={handleMeChange}><LacuneQueueScreen        /></StaffDashboardLayout>
        </RequireStaffRole>
      } />
      {/* W7 Session 2 — corpus draft review (instructor + titolare only). */}
      <Route path="/bozze"     element={
        <RequireStaffRole me={me} roles={PEDAGOGY_ROLES} fallbackTo="/staff/studenti">
          <StaffDashboardLayout me={me} onMeChange={handleMeChange}><BozzeQueueScreen         /></StaffDashboardLayout>
        </RequireStaffRole>
      } />
      <Route path="/feedback-prof" element={
        <RequireStaffRole me={me} roles={PEDAGOGY_ROLES} fallbackTo="/staff/studenti">
          <StaffDashboardLayout me={me} onMeChange={handleMeChange}><ProfFeedbackQueueScreen /></StaffDashboardLayout>
        </RequireStaffRole>
      } />
      <Route path="/metriche"  element={
        <RequireStaffRole me={me} roles={PEDAGOGY_ROLES} fallbackTo="/staff/studenti">
          <StaffDashboardLayout me={me} onMeChange={handleMeChange}><MetricheScreen           /></StaffDashboardLayout>
        </RequireStaffRole>
      } />
      {/* W6 v1.1 — Polso classe (spec §5.5). Pedagogy-roles only. */}
      <Route path="/pulse"     element={
        <RequireStaffRole me={me} roles={PEDAGOGY_ROLES} fallbackTo="/staff/studenti">
          <StaffDashboardLayout me={me} onMeChange={handleMeChange}><ClassPulseScreen         /></StaffDashboardLayout>
        </RequireStaffRole>
      } />
      <Route path="*" element={<Navigate to={defaultLanding} replace />} />
    </Routes>
  );
}

/**
 * Route guard that redirects the user to `fallbackTo` when their role is
 * NOT in the `roles` allow-list. Renders the wrapped children otherwise.
 *
 * Load-bearing privacy guarantee for the segreteria role: pedagogy URLs
 * (/staff/home, /staff/lacune, /staff/metriche, /staff/studenti/:id) must
 * not render their page chrome to a front-desk user even when pasted
 * directly into the address bar. The BE already 403s, but the FE-level
 * redirect prevents the brief render-then-error flash that user-testing
 * flagged as a leak signal.
 *
 * `me` is required (non-null) — StaffApp only mounts the routes after
 * the /me fetch resolves, so by the time this guard runs the role is known.
 */
function RequireStaffRole({
  me,
  roles,
  fallbackTo,
  children,
}: {
  me:         StaffMe;
  roles:      readonly StaffRole[];
  fallbackTo: string;
  children:   ReactNode;
}): JSX.Element {
  if (!roles.includes(me.role)) {
    return <Navigate to={fallbackTo} replace />;
  }
  return <>{children}</>;
}
